import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const GenderCards = ({
  graduatesByGender = [],
  youthGraduatesByGender = [],
  ageDistributionData = [],
}) => {
  // 🧹 Clean & normalize age data
  const cleanedAgeData = (ageDistributionData || [])
    .filter((item) => item && item.age && item.age.toLowerCase() !== "total")
    .map((item) => ({
      ...item,
      graduates: Number(item.graduates) || 0,
      femalePercent: Number(item.femalePercent) || 0,
      malePercent: Number(item.malePercent) || 0,
    }));

  // 🧮 Common helper to calculate totals
  const getTotal = (data) =>
    data.reduce((sum, d) => sum + (Number(d.graduates) || 0), 0);

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* 🟦 All Graduates by Gender */}
      <Grid item xs={12} md={3}>
        <Card sx={{ height: 500, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              All Graduates by Gender
            </Typography>

            {/* Chart */}
            <Box sx={{ flex: "0 0 260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={graduatesByGender}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="graduates"
                    label={({ gender, graduates }) =>
                      `${gender}: ${graduates.toLocaleString()}`
                    }
                    labelLine={false}
                  >
                    {graduatesByGender.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? "#2196f3" : "#e91e63"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      `${value.toLocaleString()}`,
                      "Graduates",
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {/* Scrollable details */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                flex: "1 1 auto",
                overflowY: "auto",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Graduates Comparison:
              </Typography>
              {graduatesByGender.map((g, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: i === 0 ? "#2196f3" : "#e91e63",
                      borderRadius: "50%",
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2">
                    {g.gender}: <strong>{g.rate}%</strong> (
                    {g.graduates.toLocaleString()} graduates)
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Total: {getTotal(graduatesByGender).toLocaleString()} graduates
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* 🟩 Youth Graduates by Gender */}
      <Grid item xs={12} md={3}>
        <Card sx={{ height: 500, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Youth Graduates by Gender
            </Typography>

            <Box sx={{ flex: "0 0 260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={youthGraduatesByGender}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="graduates"
                    label={({ gender, graduates }) =>
                      `${gender}: ${graduates.toLocaleString()}`
                    }
                    labelLine={false}
                  >
                    {youthGraduatesByGender.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? "#2196f3" : "#e91e63"}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      `${value.toLocaleString()}`,
                      "Youth Graduates",
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                flex: "1 1 auto",
                overflowY: "auto",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Youth Graduates Comparison:
              </Typography>
              {youthGraduatesByGender.map((g, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: i === 0 ? "#2196f3" : "#e91e63",
                      borderRadius: "50%",
                      mr: 1,
                    }}
                  />
                  <Typography variant="body2">
                    {g.gender}: <strong>{g.rate}%</strong> (
                    {g.graduates.toLocaleString()} youth)
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Total: {getTotal(youthGraduatesByGender).toLocaleString()} youth
                graduates
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* 🟧 All Graduates Age Distribution */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 500, display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              All Graduates Age Distribution 
            </Typography>

            <Box sx={{ flex: "0 0 260px" }}>
              {cleanedAgeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={cleanedAgeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ age, graduates }) =>
                        `${age} (${graduates.toLocaleString()})`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="graduates"
                    >
                      {cleanedAgeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={`hsl(${index * 60}, 70%, 50%)`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [
                        `${value.toLocaleString()} employees`,
                        "Count",
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 10, textAlign: "center" }}
                >
                  No age distribution data available
                </Typography>
              )}
            </Box>

            {/* Scrollable age summary */}
            {cleanedAgeData.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderTop: "1px solid #e0e0e0",
                  flex: "1 1 auto",
                  overflowY: "auto",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Age Group Distribution:
                </Typography>
                {cleanedAgeData.map((entry, index) => {
                  const total = getTotal(cleanedAgeData);
                  const percentage = (
                    (entry.graduates / total) *
                    100
                  ).toFixed(1);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                            borderRadius: "50%",
                            mr: 1,
                          }}
                        />
                        <Typography variant="caption">{entry.age}</Typography>
                      </Box>
                      <Typography variant="caption">
                        <strong>{entry.graduates.toLocaleString()}</strong> (
                        {percentage}%)
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Typography variant="caption" sx={{ color: "#e91e63" }}>
                          ♀ {entry.femalePercent}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#2196f3" }}>
                          ♂ {entry.malePercent}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    const highestGroup = cleanedAgeData.reduce(
                      (max, current) =>
                        current.graduates > max.graduates ? current : max
                    );
                    const firstGroup = cleanedAgeData[0];
                    const lastGroup =
                      cleanedAgeData[cleanedAgeData.length - 1];
                    const totalEmployees = getTotal(cleanedAgeData);
                    const youngPercentage = (
                      (firstGroup.graduates / totalEmployees) *
                      100
                    ).toFixed(1);
                    const seniorPercentage = (
                      (lastGroup.graduates / totalEmployees) *
                      100
                    ).toFixed(1);
                    const highestPercentage = (
                      (highestGroup.graduates / totalEmployees) *
                      100
                    ).toFixed(1);

                    return `Age Distribution Analysis: Young workforce (${firstGroup.age}) represents ${youngPercentage}% (${firstGroup.graduates.toLocaleString()}) while senior workforce (${lastGroup.age}) represents ${seniorPercentage}% (${lastGroup.graduates.toLocaleString()}). Peak concentration is in ${highestGroup.age} age group (${highestPercentage}% - ${highestGroup.graduates.toLocaleString()} employees).`;
                  })()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default GenderCards;
